// W17 · FORM FEEDBACK (SG-REBUILD-PORTFOLIO-COORDINATION-20261012-001) — the ONE behaviour set for field errors,
// the error summary, first-error focus and user-safe notice text.
//
// The React pieces (field.tsx FieldError / FieldErrorSummary, form.tsx FormMessage, alert.tsx AlertActions) render
// it; the design-compiled booking surface cannot import, so apps/root publishes these same functions to it as
// `window.__SG_FORM_FEEDBACK` (lib/design-form-feedback.ts). Nothing here touches the DOM at import time.
//
// WHEN AN ERROR SHOWS. A field's error is computed from its current value every render, and shown only while the
// field is "revealed": revealed on blur and on submit, dropped the moment its value becomes valid. So a field is
// never shouted at while it is first being typed, an errored field re-validates as it changes, and a late prefill
// (a profile that arrives after the blur) clears the error without the customer touching anything.

/** The Kit's danger red (Stripe's appearance `colorDanger` uses the same value). */
export const FIELD_ERROR_COLOR = "#C4271B";

/** The stable id of a field's message element: what the control's `aria-describedby` names. */
export function fieldMessageId(fieldId: string): string {
  return `${fieldId}-message`;
}

/** On blur: reveal the field (its error, if any, now shows). */
export function revealOnBlur(revealed: readonly string[], key: string): string[] {
  return revealed.includes(key) ? [...revealed] : [...revealed, key];
}

/** On change: a revealed field that is now valid is dropped, so its error clears at once; an invalid one stays revealed. */
export function settleOnChange(revealed: readonly string[], key: string, valid: boolean): string[] {
  return valid ? revealed.filter((k) => k !== key) : [...revealed];
}

/** On submit: every field with an error is revealed. */
export function revealOnSubmit(revealed: readonly string[], errorKeys: readonly string[]): string[] {
  return [...revealed, ...errorKeys.filter((k) => !revealed.includes(k))];
}

/** The errors on screen: the current errors of the revealed fields, in the errors' own (visual) order. */
export function visibleErrors(errors: Readonly<Record<string, string | null | undefined>>, revealed: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, message] of Object.entries(errors)) if (message && revealed.includes(key)) out[key] = message;
  return out;
}

/** The error summary's heading. */
export function errorSummaryTitle(count: number): string {
  return count === 1 ? "Check 1 detail before you continue" : `Check ${count} details before you continue`;
}

export interface FocusEnv {
  document: { getElementById(id: string): HTMLElement | null };
  matchMedia?: (query: string) => { matches: boolean };
}

export interface FocusOptions {
  /** Room left above the field for a sticky header, in px. */
  topOffset?: number;
  /** Controls that are not plain elements (Stripe's Payment Element), by the id the summary links to. */
  focusers?: Readonly<Record<string, () => void>>;
}

/**
 * Scroll to and focus the first of `ids` (callers pass them in visual order) that is on the page. The field is
 * centred with `topOffset` kept clear for the sticky header; the scroll is instant under reduced motion. Returns the
 * id focused, or null.
 */
export function focusFirstError(ids: readonly string[], env: FocusEnv, { topOffset = 96, focusers = {} }: FocusOptions = {}): string | null {
  const reduce = env.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  for (const id of ids) {
    const custom = focusers[id];
    const el = env.document.getElementById(id);
    if (!el && !custom) continue;
    if (el && !custom && typeof el.getClientRects === "function" && el.getClientRects().length === 0) continue; // not rendered
    if (el) {
      el.style.scrollMarginTop = `${topOffset}px`;
      el.scrollIntoView?.({ block: "center", behavior: reduce ? "auto" : "smooth" });
    }
    if (custom) custom();
    else el?.focus({ preventScroll: true });
    return id;
  }
  return null;
}

// Provider object ids (Stripe pi_/pm_/seti_/ch_/cus_/…, our own ord_), raw snake_case codes, stack frames, markup and
// key/secret talk never reach a customer.
const PROVIDER_ID = /\b(?:pi|pm|seti|ch|cus|re|py|src|tok|card|in|ii|price|prod|sub|evt|req|acct|cs|ord)_[A-Za-z0-9]{6,}/;
const UNSAFE = [/\bno such\b/i, /\bapi[ _-]?key\b/i, /\bsecret\b/i, /\bstack\b/i, /\bat \S+ \(.*:\d+:\d+\)/, /[{}<>]/, /\b[a-z]+_[a-z][a-z_]*\b/];

/** `raw` when it is plain, short user wording; otherwise the caller's safe `fallback`. */
export function safeUserMessage(raw: unknown, fallback: string): string {
  if (typeof raw !== "string") return fallback;
  const text = raw.trim();
  if (!text || text.length > 300) return fallback;
  if (PROVIDER_ID.test(text) || UNSAFE.some((r) => r.test(text))) return fallback;
  return text;
}
